/**
 * Copy text to the clipboard with a synchronous fallback.
 *
 * Safari/iOS WebKit is strict about clipboard access: the async Clipboard API
 * (`navigator.clipboard.writeText`) only works inside the synchronous turn of a
 * user gesture, and it can be unavailable entirely in non-secure contexts or
 * embedded webviews. When the async API is missing or rejects, we fall back to
 * the legacy `document.execCommand('copy')`, which is fully synchronous and
 * therefore always runs within the gesture.
 *
 * Call this directly from the click handler — do not `await` anything before it.
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard?.writeText &&
    window.isSecureContext
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the execCommand fallback below.
    }
  }

  if (!copyViaExecCommand(text)) {
    throw new Error('Clipboard copy failed');
  }
}

function copyViaExecCommand(text: string): boolean {
  if (typeof document === 'undefined') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Keep it out of the layout/viewport but still selectable.
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';

  document.body.appendChild(textarea);

  const selection = document.getSelection();
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let succeeded = false;
  try {
    succeeded = document.execCommand('copy');
  } catch {
    succeeded = false;
  }

  document.body.removeChild(textarea);

  // Restore any prior selection we clobbered.
  if (previousRange && selection) {
    selection.removeAllRanges();
    selection.addRange(previousRange);
  }

  return succeeded;
}
